import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, Row, Col, Button, Spinner, Container, Badge } from "react-bootstrap";
import Swal from "sweetalert2";
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";
import { FaEdit, FaTrash, FaArrowLeft, FaUser, FaPhone, FaEnvelope, FaVenusMars, FaUserTag, FaBuilding } from "react-icons/fa";

const ViewStaff = () => {
  const { id, pointindex } = useParams();
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const res = await axios.get(`${BASE_URL}staff/findById/${id}`, {
          headers: authHeader(),
        });
        setStaff(res.data);
      } catch (err) {
        Swal.fire("Error", "Failed to load staff data", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchStaff();
  }, [id]);

  const handleDelete = async () => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6"
    });

    if (confirm.isConfirmed) {
      try {
        await axios.delete(`${BASE_URL}staff/delete/${id}`, {
          headers: authHeader(),
        });
        Swal.fire("Deleted!", "Staff record has been deleted.", "success");
        navigate("/staffindex/1");
      } catch (err) {
        Swal.fire("Error", "Delete failed", "error");
      }
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!staff) {
    return (
      <Container className="mt-5">
        <Card>
          <Card.Body className="text-center">
            <h4>No staff data found</h4>
            <Link to="/staffindex/1" className="btn btn-primary mt-3">
              <FaArrowLeft className="me-2" /> Back to Staff List
            </Link>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="mt-4 mb-5">
      <Card className="shadow-sm">
        <Card.Header className=" text-primary d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <FaUser className="me-2" /> Staff Details
          </h5>
          <div className="d-flex gap-2">
            <Link to={`/staffEdit/${id}/${pointindex}`} className="btn btn-warning btn-sm">
              <FaEdit className="me-1" /> Edit
            </Link>
            <Button variant="danger" size="sm" onClick={handleDelete}>
              <FaTrash className="me-1" /> Delete
            </Button>
            <Link to="/staffindex/1" className="btn btn-secondary btn-sm">
              <FaArrowLeft className="me-1" /> Back
            </Link>
          </div>
        </Card.Header>

        <Card.Body>
          <Row className="mb-4">
            <Col md={8}>
              <h4 className="text-primary">
                {staff.firstname} {staff.lastname}
              </h4>
              <Badge bg="info" className="mb-2">
                {staff.roles?.[0]?.role || "No role assigned"}
              </Badge>
            </Col>
          </Row>

          <Row className="g-3">
            <Col md={6}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <h6 className="border-bottom pb-2 mb-3">Personal Information</h6>
                  <div className="mb-3">
                    <FaUser className="me-2 text-muted" />
                    <strong>Username: </strong>
                    <span className="ms-2">{staff.username}</span>
                  </div>
                  <div className="mb-3">
                    <FaEnvelope className="me-2 text-muted" />
                    <strong>Email: </strong>
                    <span className="ms-2">{staff.email}</span>
                  </div>
                  <div className="mb-3">
                    <FaPhone className="me-2 text-muted" />
                    <strong>Mobile: </strong>
                    <span className="ms-2">{staff.mobileno || "N/A"}</span>
                  </div>
                  <div>
                    <FaVenusMars className="me-2 text-muted" />
                    <strong>Gender: </strong>
                    <span className="ms-2">{staff.gender || "N/A"}</span>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <h6 className="border-bottom pb-2 mb-3">Organization Details</h6>
                  <div className="mb-3">
                    <FaUserTag className="me-2 text-muted" />
                    <strong>Role: </strong>
                    <span className="ms-2">{staff.roles?.[0]?.role || "N/A"}</span>
                  </div>
                  <div>
                    <FaBuilding className="me-2 text-muted" />
                    <strong>Department: </strong>
                    <span className="ms-2">{staff.department?.name || "N/A"}</span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ViewStaff;

